import { createFileRoute } from "@tanstack/react-router";
import { fetchStrapiAPI } from "@/services/strapi";
import { Link } from "@tanstack/react-router";
import Navbar from "@/components/Home/NavBar";
import ScrollAnimation from "@/components/Home/Animations";
import Footer from "@/components/Home/Footer";
import Markdown from "react-markdown";
interface BlogPost {
  id: number;
  documentId: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  publishedDate: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  featuredImage?: {
    data: {
      attributes: {
        url: string;
        alternativeText?: string;
      };
    } | null;
  };
  author?: {
    data: {
      attributes: {
        name: string;
        picture?: {
          data: {
            attributes: {
              url: string;
            };
          };
        };
      };
    } | null;
  };
}

interface BlogListResponse {
  data: BlogPost[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export const Route = createFileRoute("/blog/")({
  component: BlogIndexPage,
  head: ({ loaderData }) => {
    if (!loaderData || !("blogPosts" in loaderData)) {
      return {
        meta: [
          {
            title: "Blog | aiEmailAgent",
          },
          {
            description:
              "Stay up to date with the latest trends in AI-powered sales communication",
          },
        ],
      };
    }
    return {
      meta: [
        {
          title: "Blog | aiEmailAgent",
        },
        {
          description:
            "Stay up to date with the latest trends in AI-powered sales communication",
        },
      ],
    };
  },
  loader: async ({ location }) => {
    try {
      const searchParams = new URLSearchParams(location.search);
      const page = parseInt(searchParams.get("page") ?? "1", 10);
      const pageSize = parseInt(searchParams.get("pageSize") ?? "6", 10);
      const data = await fetchStrapiAPI<BlogListResponse>(
        `/blog-posts?pagination[page]=${page}&pagination[pageSize]=${pageSize}`,
      );
      return { blogPosts: data };
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      return { blogPosts: null };
    }
  },
});

function BlogIndexPage() {
  const { blogPosts } = Route.useLoaderData() as {
    blogPosts: BlogListResponse;
  };

  if (!blogPosts) {
    return <div>Error loading blog posts</div>;
  }

  return (
    <ScrollAnimation>
      <Navbar />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="py-20 px-6 md:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 section-fade">
              <div className="inline-block px-3 py-1 mb-6 rounded-full glass text-sm font-medium">
                Our Blog
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Latest <span className="text-gradient">Insights</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Stay up to date with the latest trends in AI-powered sales
                communication
              </p>
            </div>

            {/* Blog Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.data.map((post: BlogPost) => (
                <BlogPostCard key={post.id} post={post} />
              ))}
            </div>

            {/* Pagination - if needed */}
            {blogPosts.meta.pagination.pageCount > 1 && (
              <div className="mt-12 flex justify-center">
                <Link
                  to="/blog"
                  className="btn-primary"
                  search={{
                    page: blogPosts.meta.pagination.page + 1,
                    pageSize: 6,
                  }}
                >
                  Read More
                </Link>
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </ScrollAnimation>
  );
}

function BlogPostCard({ post }: { post: BlogPost }) {
  const formattedDate = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(post.publishedDate));

  return (
    <Link
      to={`/blog/$slug`}
      params={{ slug: post.slug }}
      className="glass rounded-xl overflow-hidden hover:shadow-lg transition-shadow duration-300 block"
    >
      {post.featuredImage?.data?.attributes?.url ? (
        <div className="aspect-video w-full overflow-hidden">
          <img
            src={
              post.featuredImage.data.attributes.url.includes("http")
                ? post.featuredImage.data.attributes.url
                : "/og-image.svg"
            }
            alt={
              post.featuredImage.data.attributes.alternativeText || post.title
            }
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="aspect-video w-full bg-muted">
          <img
            src="/og-image.svg"
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-6">
        <div className="text-sm text-muted-foreground mb-3">
          {formattedDate}
        </div>
        <h2 className="text-xl font-semibold mb-3 line-clamp-2">
          {post.title}
        </h2>
        <p className="text-muted-foreground line-clamp-3">
          <Markdown>{post.excerpt || post.content.slice(0, 150)}</Markdown>
        </p>

        {post.author?.data?.attributes && (
          <div className="mt-6 flex items-center gap-3">
            {post.author.data.attributes.picture?.data?.attributes?.url ? (
              <img
                src={post.author.data.attributes.picture.data.attributes.url}
                alt={post.author.data.attributes.name}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-xs font-medium">
                  {post.author.data.attributes.name.charAt(0)}
                </span>
              </div>
            )}
            <span className="text-sm font-medium">
              {post.author.data.attributes.name}
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}

export default BlogIndexPage;
